"use client";

import React, { useState, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { ensureConversationId, sendMessageToChatAPI } from "@/lib/chat-client";
import { ENABLE_CHAT } from "@/lib/environmentConfig";
import { formatMarkdownResponse } from "@/lib/markdown-utils";

const SEND_ICON = "/assets/chatBot/send.svg";

const LiveChat = ({ isOpen, onClose }) => {
  if (!ENABLE_CHAT) return null;

  const [message, setMessage] = useState("");
  const [chatStarted, setChatStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  const handleAvatarClick = () => {
    if (chatStarted) {
      setChatStarted(false);
    } else {
      if (messages.length > 0) {
        setChatStarted(true);
      }
    }
  };

  const suggestionCards = [
    { id: 1, text: "What services does your company provide?" },
    { id: 2, text: "How long has your company been in the IT industry?" },
    { id: 3, text: "Can you show me your latest projects?" },
    { id: 4, text: "Who are your key clients or partners?" },
  ];

  // Reusable Suggestion Card Component
  const SuggestionCard = ({ text, onClick }) => (
    <div
      onClick={onClick}
      className="flex-1 h-[138px] dark:bg-[#18181B] bg-[#FFFFFF] rounded-2xl p-4 suggestion-card border dark:border-[#27272B] border-transparent hover:border-[#FF1CF780] hover:shadow-[0_4px_4px_#FF1CF733] transition-all duration-150 ease-in cursor-pointer"
    >
      <div className="flex flex-col gap-2.5">
        <div className="w-6 h-6 flex items-center justify-center">
          <Image
            src="/assets/chatBot/star.svg"
            alt="Star icon"
            width={23}
            height={23}
            className="object-contain"
          />
        </div>
        <div className="w-11/12 h-px border-t border-[#FF1CF7]"></div>
        <p className="font-['Manrope'] text-xs leading-[18px] tracking-[-0.01em] dark:text-white text-[#5A5A6E]">
          {text}
        </p>
      </div>
    </div>
  );

  const handleSendMessage = (overrideText) => {
    const text = (overrideText ?? message).trim();
    if (!text) return null;
    setChatStarted(true);
    const userMsg = {
      id: Date.now(),
      sender: "user",
      text,
      timestamp: new Date(),
    };
    setMessages((m) => [...m, userMsg]);
    if (!overrideText) setMessage("");
    setIsTyping(true);

    // Auto-scroll to user message
    setTimeout(() => {
      try {
        const el = document.getElementById(`msg-${userMsg.id}`);
        if (el && messagesRef.current) {
          const container = messagesRef.current;
          const offset = el.offsetTop - container.offsetTop;
          container.scrollTo({ top: offset, behavior: "smooth" });
        }
      } catch (e) {}
    }, 30);

    (async () => {
      try {
        const reply = await sendMessageToChatAPI(text, ensureConversationId());
        const botMsg = {
          id: Date.now() + 1,
          sender: "bot",
          data: {
            type: "formatted",
            html: formatMarkdownResponse(reply),
            text: reply,
          },
          timestamp: new Date(),
        };
        setMessages((m) => [...m, botMsg]);
        setTimeout(() => {
          try {
            const el = document.getElementById(`msg-${botMsg.id}`);
            if (el && messagesRef.current) {
              const container = messagesRef.current;
              const offset = el.offsetTop - container.offsetTop;
              container.scrollTo({ top: offset, behavior: "smooth" });
            }
          } catch (e) {}
        }, 30);
      } catch (e) {
        setMessages((m) => [
          ...m,
          {
            id: Date.now() + 2,
            sender: "bot",
            data: { type: "plain", text: "Chat service unavailable." },
            timestamp: new Date(),
          },
        ]);
        setTimeout(() => {
          try {
            const last = document.getElementById(`msg-${Date.now() + 2}`);
            if (last && messagesRef.current) {
              const container = messagesRef.current;
              const offset = last.offsetTop - container.offsetTop;
              container.scrollTo({ top: offset, behavior: "smooth" });
            }
          } catch (err) {}
        }, 30);
      } finally {
        setIsTyping(false);
      }
    })();

    return userMsg.id;
  };

  const messagesRef = useRef(null);

  const handleSuggestionClick = async (suggestionText) => {
    setChatStarted(true);
    const userId = handleSendMessage(suggestionText);
    if (!userId) return;
    setTimeout(() => {
      try {
        const el = document.getElementById(`msg-${userId}`);
        if (el && messagesRef.current) {
          const container = messagesRef.current;
          const offset = el.offsetTop - container.offsetTop;
          container.scrollTo({ top: offset, behavior: "smooth" });
        }
      } catch (e) {}
    }, 50);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const TypingIndicator = () => (
    <div className="flex gap-2.5 message-bot">
      <div className="w-[30px] h-[30px] shrink-0 mt-1 relative">
        <Image
          src="/assets/chatBot/ellipse-avatar.svg"
          alt="Bot"
          fill
          className="object-contain"
        />
        <div className="absolute inset-1 rounded-full overflow-hidden">
          <Image
            src="/assets/chatBot/chatBot.webp"
            alt="AI Assistant"
            fill
            className="object-contain"
            sizes="30px"
            quality={60}
            priority={false}
            loading="lazy"
          />
        </div>
      </div>
      <div className="flex-1 dark:bg-[#18181B] bg-[#FFFFFF] border border-[#8051EA] chat-bubble-bot p-3 rounded-xl rounded-tl-sm">
        <div className="flex space-x-1">
          <div
            className="w-2 h-2 bg-[#FF1CF7] rounded-full animate-bounce"
            style={{ animationDelay: "0ms" }}
          ></div>
          <div
            className="w-2 h-2 bg-[#FF1CF7] rounded-full animate-bounce"
            style={{ animationDelay: "150ms" }}
          ></div>
          <div
            className="w-2 h-2 bg-[#FF1CF7] rounded-full animate-bounce"
            style={{ animationDelay: "300ms" }}
          ></div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-9999 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      ></div>

      {/* Chat sidebar */}
      <div
        className={`fixed right-1 bottom-1 z-10000 transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{
          width: "min(90vw, 393px)",
          height: "min(90vh, 752px)",
          maxHeight: "90vh",
          overflowY: "auto",
          aspectRatio: "393/752",
        }}
      >
        <div className="relative w-full h-full">
          {/* a pink section behind the chat for the bottom right pink part */}
          <div className="absolute top-0 right-0 w-[98%] h-20 bg-gradient-to-r from-[#DF27DF] to-[#FF1CF7] rounded-t-[20px] z-1 overflow-hidden border-none" />

          {/* Chat Container */}
          <div className="absolute top-0 left-0 w-full h-full rounded-[20px] shadow-2xl overflow-hidden flex flex-col border-none z-2">
            {/* Header */}
            <div className="relative w-full h-[60px] rounded-t-[17px] overflow-hidden border-none">
              {/* Upper half - Pink background */}
              {/* <div className="absolute top-0 left-2 w-full h-12 bg-gradient-to-r from-[#DF27DF] to-[#FF1CF7] border-none"></div> */}

              {/* Lower half - Black background */}
              <div className="absolute -bottom-2 left-0 w-full h-10 dark:bg-[#111114] bg-[#F2E9FF] border-none"></div>

              {/* Avatar Section - Upper black with tr rounded */}
              <div className="absolute left-0 top-0 w-[90px] h-8 dark:bg-[#111114] bg-[#F2E9FF] rounded-tr-3xl z-15 border-none"></div>

              {/* Live Support Section - Lower pink with bl rounded   (bottom pink) */}
              <div className="absolute left-[90px] bottom-0 right-0 h-[33px] bg-gradient-to-r from-[#DF27DF] to-[#FF1CF7] rounded-bl-full z-20 border-none"></div>

              <div
                className="absolute left-5 top-[9px] w-[50px] h-[50px] z-20 cursor-pointer transform transition-transform duration-200 hover:scale-105 border-none"
                onClick={handleAvatarClick}
                role="button"
                aria-label={
                  chatStarted ? "Show suggestions" : "Show chat history"
                }
              >
                {/* Ellipse background */}
                <Image
                  src="/assets/chatBot/ellipse-avatar.svg"
                  alt="Avatar Background"
                  fill
                  className="object-contain"
                />
                {/* Chatbot image overlay */}
                <div className="absolute inset-2 rounded-full overflow-hidden">
                  <Image
                    src="/assets/chatBot/chatBot.webp"
                    alt="AI Assistant"
                    fill
                    className="object-contain"
                  />
                </div>
              </div>

              {/* Live Support Text */}
              <div className="absolute right-4 top-0 h-full flex items-center z-20">
                <div className="px-12 py-6 relative">
                  {/* Title */}
                  <h1 className="text-white font-['Alexandria'] font-extrabold text-[16px] small:text-[20px] lg:text-[24px] leading-[29px] tracking-[0.1em] whitespace-nowrap">
                    LIVE SUPPORT
                  </h1>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="absolute right-3 top-[7px] w-5 h-5 text-[#DDDDDD] hover:text-white cursor-pointer transition-colors z-20"
                aria-label="Close chat"
                title="Close chat"
              >
                <span aria-hidden="true" className="text-white text-[18px]">
                  ×
                </span>
              </button>
            </div>

            {/* Chat Area */}
            <div className="flex-1 relative overflow-hidden dark:bg-[#111114] bg-[#F2E9FF] rounded-tr-[20px]">
              {!chatStarted ? (
                // Initial State with Suggestions
                <div className="flex flex-col items-center gap-8 justify-end h-full">
                  {/* Welcome Header */}
                  <h2 className="font-medium font-alexandria text-[32px] sm:text-[35px] leading-[38px] tracking-[-0.02em] max-w-[353px]">
                    <span
                      style={{
                        background:
                          "linear-gradient(90deg, #BA9BFF 0%, #0E4DEC 91.21%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      Ask whatever
                    </span>
                    <span className="dark:text-white text-[#1A1A1A]">
                      {" "}
                      you want to help with..
                    </span>
                  </h2>

                  {/* Suggestion Cards */}
                  <div className="grid grid-cols-2 gap-3 w-[90vw] small:w-[353px]">
                    {suggestionCards.slice(0, 4).map((card) => (
                      <SuggestionCard
                        key={card.id}
                        text={card.text}
                        onClick={() => handleSuggestionClick(card.text)}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div
                  ref={messagesRef}
                  className="p-5 sm:p-5 space-y-4 h-full overflow-y-auto chat-messages pt-6"
                >
                  {messages.map((msg) =>
                    msg.sender === "user" ? (
                      <div
                        key={msg.id}
                        id={`msg-${msg.id}`}
                        className="flex justify-end message-user mb-4"
                      >
                        <div className="max-w-[280px] dark:bg-[#1A1C22] bg-[#F6F6F6] dark:border dark:border-[#27272B] chat-bubble-user p-3 rounded-2xl rounded-tr-xs">
                          <p className="font-['Manrope'] text-sm leading-5 tracking-[-0.01em] dark:text-white text-[#5A5A6E]">
                            {msg.text}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        key={msg.id}
                        id={`msg-${msg.id}`}
                        className="flex gap-2.5 message-bot"
                      >
                        <div className="w-[30px] h-[30px] shrink-0 mt-1 relative">
                          <Image
                            src="/assets/chatBot/ellipse-avatar.svg"
                            alt="Bot"
                            fill
                            className="object-contain"
                          />
                          <div className="absolute inset-1 rounded-full overflow-hidden">
                            <Image
                              src="/assets/chatBot/chatBot.webp"
                              alt="AI Assistant"
                              fill
                              className="object-contain"
                            />
                          </div>
                        </div>
                        <div className="flex-1 dark:bg-[#18181B] bg-[#FFFFFF] border border-[#8051EA] chat-bubble-bot p-4 rounded-2xl rounded-tl-sm">
                          {(() => {
                            const html =
                              msg.data?.html ??
                              (msg.data?.text
                                ? formatMarkdownResponse(msg.data.text)
                                : null);
                            if (html) {
                              return (
                                <div
                                  className="chat-markdown max-w-none text-sm leading-6"
                                  dangerouslySetInnerHTML={{ __html: html }}
                                />
                              );
                            }
                            return (
                              <p className="font-['Manrope'] text-sm leading-6 tracking-[-0.01em] text-white whitespace-pre-line">
                                {msg.data?.text ?? ""}
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                    ),
                  )}
                  {isTyping && <TypingIndicator />}
                </div>
              )}
            </div>

            {/* Bottom Container */}
            <div className="w-full h-[100px] flex flex-col mt-auto dark:bg-[#111114] bg-[#F2E9FF] border-none">
              {/* Input Container */}
              <div className="flex items-start gap-1 p-3 px-5 h-16">
                {/* Input Field */}
                <div className="flex-1 h-11">
                  <div className="w-full h-full dark:bg-[#1C1C1F] bg-[#FCFAFF] border dark:border-[#27272B] border-[#BFBFBF] rounded-full flex items-center px-3 gap-2 chat-input">
                    <input
                      type="text"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask anything..."
                      className="flex-1 bg-transparent text-sm font-['Manrope'] dark:text-white text-black placeholder-[#B5B5B5] outline-none"
                    />
                  </div>
                </div>

                {/* Send Button */}
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!message.trim()}
                  className="w-11 h-11 dark:bg-[#DF27DF] bg-[#CBA3FF] rounded-full flex items-center justify-center send-button transition-all duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed dark:hover:bg-[#FF1CF7] hover:bg-[#D39FFF]"
                  aria-label="Send message"
                  about="Send message"
                >
                  <Image
                    src={SEND_ICON}
                    alt="Send"
                    width={16}
                    height={16}
                    className="text-[#2E2E2E]"
                    priority={false}
                  />
                </button>
              </div>

              {/* Navigation Bar */}
              <div className="flex justify-center items-start backdrop-blur-[12.5px] mb-5">
                <Link
                  href="/chat"
                  className="text-xs font-['Manrope'] dark:text-[#E6E6E6] text-[#5A5A6E] underline dark:hover:text-white hover:text-[#0D0D14] transition-colors"
                >
                  Open in full chat view
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LiveChat;
